import React from 'react';
import { toast } from '@heroui/react';
import {
    showLocalizedToast,
    type ToastCopy,
} from '@/components/localizedToast';
import { countParam } from '@/pages/catalogImport/notices';
import {
    canUndo as canUndoOf,
    latestBulkAction,
    useCatalogImport,
} from '@/pages/catalogImport/store';
import {
    ISSUE_KIND_FIX,
    isBarcodeIssueKind,
    isFixableIssueKind,
    issueEntriesOf,
    labelsNoticeOf,
    type BarcodeLabels,
    type FixableIssueKind,
    type IssueEntry,
    type IssueKind,
} from './importIssues';

/**
 * What a bulk fix's toast says it did, by kind: the rows it changed ("One
 * value given to 12 rows"), or for a barcode case the products it gives a
 * generated barcode ("152 products get a generated barcode").
 */
const APPLIED_KEY = {
    imei: 'catalogImport.products.bulk.barcodesGenerated',
    scientificNotation: 'catalogImport.products.bulk.barcodesGenerated',
    notDigits: 'catalogImport.products.bulk.barcodesGenerated',
    variantFields: 'catalogImport.products.bulk.variantFields',
    groupFields: 'catalogImport.products.bulk.groupFields',
    sharedBarcode: 'catalogImport.products.bulk.sharedBarcode',
} as const satisfies Record<FixableIssueKind, string>;

/** The line under a bulk toast, unless the action says something of its own. */
const UNDO_DESCRIPTION: ToastCopy = {
    key: 'catalogImport.products.bulk.description',
};

/** A barcode case's restocked products: they keep their barcode (#232 rule 2). */
const RESTOCKS_KEPT_KEY = 'catalogImport.products.bulk.restocksKeep';

/** A bulk change of the exclusions: Exclude all, or Include all back. */
export type BulkExclusion = 'excluded' | 'included';

/** What an exclusion toast says it did, and the line under it. */
const EXCLUSION_TOAST = {
    excluded: {
        key: 'catalogImport.products.bulk.excluded',
        description: 'catalogImport.products.bulk.excludedDescription',
    },
    included: {
        key: 'catalogImport.products.bulk.included',
        description: 'catalogImport.products.bulk.description',
    },
} as const satisfies Record<
    BulkExclusion,
    { key: string; description: string }
>;

/**
 * How long a bulk action's toast stays: long enough to reach its Undo. The
 * summary's Undo stays until the import either way.
 */
export const BULK_TOAST_TIMEOUT = 10_000;

export type UseBulkFixesOptions = {
    /** A check is running: the summary's counts are about to change, so the fixes wait. */
    isChecking: boolean;
    /** The import is running: nothing changes until it answers. */
    isImporting: boolean;
    /** Asks the step for a check once the store holds the new lines. */
    onRequestRecheck: () => void;
};

export type UseBulkFixesResult = {
    /** The kinds the last check found, each with its count and fix. */
    issues: IssueEntry[];
    /** The labels notice (`labelsNoticeOf`); `null` for a file that needs none. */
    labels: BarcodeLabels | null;
    /** The fixes wait (a check or the import is running). */
    isFixDisabled: boolean;
    /** A bulk action was done and not undone yet, and the import is not running. */
    canUndo: boolean;
    /**
     * Applies a kind's default to every group of it, re-checks, and toasts
     * how many rows it changed with Undo.
     */
    onApplyFix: (kind: IssueKind) => void;
    /**
     * Toasts a drawer group pick the store has applied (`applyGroupPick`,
     * which it snapshots): how many rows it changed, with Undo; or that it
     * changed none. The drawer asks for the check itself.
     */
    onGroupFixApplied: (changed: number) => void;
    /**
     * Toasts a bulk change of the exclusions the store has applied
     * (`excludeAll` / `includeAll`, which it snapshots): how many rows, with
     * Undo (bos-app#220). Exclude all's line says where the rows went.
     */
    onExclusionApplied: (kind: BulkExclusion, changed: number) => void;
    /**
     * Undoes the most recent bulk action not undone yet, re-checks, and
     * toasts how many rows it restored.
     */
    onUndo: () => void;
    /**
     * The suggested-fixes dialog is open (`held`), or closed again: while it
     * is, the last bulk toast is closed and every Undo here does nothing, so
     * none takes back the step that dialog stands on.
     */
    holdUndo: (held: boolean) => void;
};

/**
 * The issues summary's logic (bos-app#213, US-02..US-06, US-08): the kinds
 * of problem the last check found (`issueEntriesOf`), their bulk fixes and
 * the one-step Undo, all through the store's actions (#217).
 *
 * - **A fix** (`onApplyFix`) applies its kind's default (`applyFix`), which
 *   the store snapshots first, then asks for a check, as the drawer's "Use
 *   for all rows" does, so the problem leaves the summary and the table
 *   when the check answers. A success toast names the rows it changed and
 *   has **Undo**. A fix that changed no row toasts so, and checks nothing.
 * - **Undo** (`onUndo`, the toast's and the summary's) restores the latest
 *   snapshot (`undo`) and checks again, so the grid never shows a preview
 *   of the values it replaced; a second Undo restores the action before.
 *   Its toast names the rows restored, and says so when edits made after
 *   the action went with it (more rows restored than the action changed).
 * - **The drawer's group pick** (bos-app#219) is a bulk action too: the
 *   drawer applies it and asks for the check, and `onGroupFixApplied`
 *   toasts its count with the same Undo.
 * - **Exclude all and Include all back** (bos-app#220) are bulk actions as
 *   well: the step applies them, and `onExclusionApplied` toasts the count
 *   with the same Undo ("931 rows excluded · They are listed under
 *   Excluded.").
 * - **One toast at a time:** a new bulk toast closes the last one, and
 *   leaving the step closes it, so an Undo is never pressed for a step
 *   that is gone. The suggested-fixes dialog holds the Undo (`holdUndo`):
 *   it closes the toast, and Undo waits until the dialog closes.
 * - While a check runs the fixes wait (the counts are about to change);
 *   while the import runs nothing does.
 */
export const useBulkFixes = ({
    isChecking,
    isImporting,
    onRequestRecheck,
}: UseBulkFixesOptions): UseBulkFixesResult => {
    const { state, actions } = useCatalogImport();

    const issues = React.useMemo(
        () =>
            issueEntriesOf({
                lines: state.lines,
                preview: state.preview,
                exclusions: state.exclusions,
            }),
        [state.exclusions, state.lines, state.preview],
    );
    const labels = React.useMemo(
        () => labelsNoticeOf(state.preview, issues),
        [issues, state.preview],
    );

    // Read when a toast's Undo is pressed, which may be renders later.
    const latest = React.useRef({ state, isImporting });
    latest.current = { state, isImporting };
    const toastId = React.useRef<string | null>(null);
    const isUndoHeld = React.useRef(false);

    const closeToast = React.useCallback(() => {
        if (toastId.current !== null) {
            toast.close(toastId.current);
            toastId.current = null;
        }
    }, []);

    const showToast = React.useCallback(
        (show: () => string) => {
            closeToast();
            toastId.current = show();
        },
        [closeToast],
    );

    const holdUndo = React.useCallback(
        (held: boolean) => {
            isUndoHeld.current = held;
            if (held) {
                closeToast();
            }
        },
        [closeToast],
    );

    React.useEffect(
        () => () => {
            if (toastId.current !== null) {
                toast.close(toastId.current);
            }
        },
        [],
    );

    const onUndo = React.useCallback(() => {
        const { state: current, isImporting: importing } = latest.current;
        const action = latestBulkAction(current);
        if (importing || isUndoHeld.current || action === null) {
            return;
        }
        const restored = actions.undo();
        onRequestRecheck();
        showToast(() =>
            showLocalizedToast(
                'success',
                {
                    key: 'catalogImport.products.bulk.undone',
                    params: { count: restored, rows: countParam(restored) },
                },
                restored > action.rows
                    ? {
                          description: {
                              key: 'catalogImport.products.bulk.undoneEdits',
                          },
                      }
                    : {},
            ),
        );
    }, [actions, onRequestRecheck, showToast]);

    /**
     * The toast of a bulk action that changed `changed` rows, with Undo:
     * its title counts `shown` (the products a barcode case's fix gives a
     * generated barcode), else the rows; `description` is the line under
     * it (that it can be undone, unless the action says something of its
     * own).
     */
    const showApplied = React.useCallback(
        (
            key: string,
            changed: number,
            description: ToastCopy = UNDO_DESCRIPTION,
            shown = changed,
        ) => {
            if (changed === 0) {
                showToast(() =>
                    showLocalizedToast('info', {
                        key: 'catalogImport.products.bulk.nothingChanged',
                    }),
                );
                return;
            }
            showToast(() =>
                showLocalizedToast(
                    'success',
                    {
                        key,
                        params: { count: shown, rows: countParam(shown) },
                    },
                    {
                        description,
                        timeout: BULK_TOAST_TIMEOUT,
                        action: {
                            label: { key: 'catalogImport.products.bulk.undo' },
                            onPress: onUndo,
                        },
                    },
                ),
            );
        },
        [onUndo, showToast],
    );

    const onApplyFix = React.useCallback(
        (kind: IssueKind) => {
            // The extra barcodes have no fix (the import keeps them), and
            // nor has a kind with no default.
            if (!isFixableIssueKind(kind) || isChecking || isImporting) {
                return;
            }
            const entry = issues.find((candidate) => candidate.kind === kind);
            const changed = actions.applyFix(ISSUE_KIND_FIX[kind]);
            if (changed > 0) {
                onRequestRecheck();
            }
            if (!isBarcodeIssueKind(kind) || entry === undefined) {
                showApplied(APPLIED_KEY[kind], changed);
                return;
            }
            // A barcode case: the products given a generated barcode (its
            // card's count), and the restocked ones apart, which keep the
            // barcode they have (#232 rule 2).
            const restocks = entry.restocks ?? 0;
            const generated = entry.count - restocks;
            const kept: ToastCopy = {
                key: RESTOCKS_KEPT_KEY,
                params: { count: restocks, rows: countParam(restocks) },
            };
            if (generated > 0) {
                showApplied(
                    APPLIED_KEY[kind],
                    changed,
                    restocks > 0 ? kept : UNDO_DESCRIPTION,
                    generated,
                );
            } else {
                showApplied(
                    RESTOCKS_KEPT_KEY,
                    changed,
                    UNDO_DESCRIPTION,
                    restocks,
                );
            }
        },
        [
            actions,
            isChecking,
            isImporting,
            issues,
            onRequestRecheck,
            showApplied,
        ],
    );

    const onGroupFixApplied = React.useCallback(
        (changed: number) =>
            showApplied('catalogImport.products.bulk.groupApplied', changed),
        [showApplied],
    );

    const onExclusionApplied = React.useCallback(
        (kind: BulkExclusion, changed: number) =>
            showApplied(EXCLUSION_TOAST[kind].key, changed, {
                key: EXCLUSION_TOAST[kind].description,
            }),
        [showApplied],
    );

    return {
        issues,
        labels,
        isFixDisabled: isChecking || isImporting,
        canUndo: canUndoOf(state) && !isImporting,
        onApplyFix,
        onGroupFixApplied,
        onExclusionApplied,
        onUndo,
        holdUndo,
    };
};
